import {
  IconButton,
  CloseIcon,
  CalendarIcon,
  ChevronDownIcon,
} from '@buffer-mono/popcorn'
import type * as React from 'react'
import { useState } from 'react'

import DateTimeSlotPicker from '../DateTimeSlotPicker'
import Dropdown, { DropdownContent, DropdownTrigger } from '../Dropdown'
import styles from './InlineScheduler.module.css'
import type { AppStoreMetaData, Timeslot } from '../../stores/types'
import dayjs from 'dayjs'
import ComposerActionCreators from '../../action-creators/ComposerActionCreators'

interface InlineSchedulerProps {
  allowRemoveSchedule?: boolean
  scheduledAt: number | null
  timezone?: string
  userData: {
    uses24hTime: boolean
    weekStartsMonday: boolean
  }
  metaData: AppStoreMetaData
  doSelectedProfilesHaveSlots: boolean
  draftMode: boolean
  isPinnedToSlot: boolean
  shouldDisplayTime?: boolean
  isSlotPickingAvailable?: boolean
  availableSchedulesSlotsForDay: false | undefined | Timeslot[]
}

export const InlineScheduler: React.FC<InlineSchedulerProps> = ({
  scheduledAt,
  timezone,
  userData,
  isPinnedToSlot,
  doSelectedProfilesHaveSlots,
  draftMode,
  metaData,
  isSlotPickingAvailable,
  shouldDisplayTime,
  availableSchedulesSlotsForDay,
  allowRemoveSchedule = false,
}) => {
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false)

  /**
   * DateTimeSlotPicker is displayed within a DropDown component, which takes care
   * of showing/hiding it when necessary. To do that, the DropDown component
   * listens to clicks in the whole document, and when a click happens on a
   * DOM element outside of DropdownContent, it is hidden. That's awesome, but
   * in some situations with DateTimeSlotPicker, users will click on buttons, and
   * the UI will change in such a way that the buttons that were clicked will
   * disappear: when that's the case, the DropDown component will wrongly deduct
   * that the click happened outside of the DropDown, and will proceed to hiding
   * the DateTimeSlotPicker.
   * To prevent this, we stop click event bubbling at the DateTimeSlotPicker level :)
   */
  const onDateTimeSlotPickerClick = (
    e: React.MouseEvent<HTMLDivElement>,
  ): void => e.stopPropagation()

  const onDateTimeSlotPickerChange = (
    selectedDateTime: dayjs.Dayjs,
    isPinnedToSlot: boolean,
  ): void => {
    const scheduledAtUnix = selectedDateTime.unix()
    ComposerActionCreators.updateDraftsScheduledAt(
      scheduledAtUnix,
      isPinnedToSlot,
    )
  }

  const collapseDropdown = (): void => {
    setIsDropdownExpanded(false)
  }

  const expandDropdown = (): void => {
    setIsDropdownExpanded(true)
  }

  const onRemoveSchedule = (): void => {
    ComposerActionCreators.updateDraftsScheduledAt(null, false)
    collapseDropdown()
  }

  let scheduledAtMoment: dayjs.Dayjs | undefined
  let humanReadableScheduledAt: string | undefined

  if (scheduledAt) {
    scheduledAtMoment = dayjs.unix(scheduledAt)
    if (timezone) scheduledAtMoment = scheduledAtMoment.tz(timezone)

    const humanReadableFormat = userData.uses24hTime
      ? 'MMM D, H:mm'
      : 'MMM D, h:mm A'
    humanReadableScheduledAt = scheduledAtMoment.format(humanReadableFormat)
  }

  // TODO: Add support for removing schedule for non-draft updates
  //       Currently the updates_controller.update method only supports
  //       two cases for chaning due_at in non-drafts:
  //       1. not scheduled to custom scheduled or pinned
  //       2. pinned to unpinned (passing pinned as false)
  //       We need to add support to simply remove custom time and
  //       go back to the queue
  const showRemoveSchedule =
    (allowRemoveSchedule || draftMode) && scheduledAtMoment

  if (!scheduledAt && !shouldDisplayTime) {
    return null
  }

  return (
    <div className={styles.postSchedule} data-testid="inline-scheduler">
      <Dropdown
        isDropdownExpanded={isDropdownExpanded}
        onHide={collapseDropdown}
        onShow={expandDropdown}
        className={styles.inlineDropdownContainer}
      >
        <DropdownTrigger
          className={styles.scheduleEditButton}
          data-testid="inline-scheduler-edit-button"
        >
          <CalendarIcon size="small" />
          {scheduledAtMoment ? humanReadableScheduledAt : 'Set Time'}
          <ChevronDownIcon size="small" />
        </DropdownTrigger>
        <DropdownContent className={styles.topAlignedDropdownContent}>
          <DateTimeSlotPicker
            key={String(scheduledAtMoment?.toISOString()) + String(timezone)}
            onClick={onDateTimeSlotPickerClick}
            onChange={onDateTimeSlotPickerChange}
            onSubmit={collapseDropdown}
            shouldUse24hTime={userData.uses24hTime}
            timezone={timezone}
            weekStartsMonday={userData.weekStartsMonday}
            initialDateTime={scheduledAtMoment}
            isSlotPickingAvailable={isSlotPickingAvailable}
            availableSchedulesSlotsForDay={availableSchedulesSlotsForDay}
            isPinnedToSlot={isPinnedToSlot}
            metaData={metaData}
            submitButtonCopy="Done"
            doSelectedProfilesHaveSlots={doSelectedProfilesHaveSlots}
            showDraftScheduleNotice={draftMode}
          />
        </DropdownContent>
      </Dropdown>
      {showRemoveSchedule && (
        <IconButton
          data-testid="remove-schedule-time"
          size="small"
          label="Remove"
          variant="tertiary"
          onClick={onRemoveSchedule}
          className={styles.scheduleRemoveButton}
        >
          <CloseIcon color="subtle" size="xsmall" />
        </IconButton>
      )}
    </div>
  )
}
